import type {
  CommittedRow,
  Department,
  InitializeAddOrgFunctionsRequest,
  InitializeAddOrgFunctionsResponse,
  InitializeMembershipRemoval
} from '@bliks-io/contracts';
import { departmentIdToWire } from '@/lib/department-ref';
import type { ParsedMembershipRow, ParsedOrgFunctionRow } from '@/lib/excel';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';

/**
 * The ONE wire mapping for the Funktionen/Teams surface — shared by the
 * silent autosave flush and the Weiter submit so the two can never send
 * different data for the same draft. Kind-by-surface stays on the wire
 * (ADR 0010): each step fills exactly one of the two arrays. A row carrying
 * an entityId is sent as `_id` so the backend edits in place (a rename flows
 * back to users' Stelle) instead of creating a duplicate; the department id
 * maps back to its current abbreviation (an empty cell stays empty).
 * Removals (deleted persisted rows / unlinked flushed memberships) are
 * Weiter-only — the silent flush never sends them.
 */
export function buildOrgFunctionsRequest(config: {
  kind: 'role' | 'team';
  rows: readonly ParsedOrgFunctionRow[];
  memberships: readonly ParsedMembershipRow[];
  departments: Department[];
  removals?: {
    entityIds: readonly string[];
    memberships: readonly InitializeMembershipRemoval[];
  };
}): InitializeAddOrgFunctionsRequest {
  const { kind, rows, memberships, departments, removals } = config;
  const rowData = rows.map((row) => ({
    ...row.data,
    department: row.data.department
      ? departmentIdToWire(row.data.department, departments)
      : row.data.department,
    ...(row.entityId && { _id: row.entityId })
  }));
  return {
    functions: kind === 'role' ? rowData : [],
    teams: kind === 'team' ? rowData : [],
    memberships: memberships.map((row) => row.data),
    ...(removals && removals.entityIds.length > 0 && { removals: [...removals.entityIds] }),
    ...(removals &&
      removals.memberships.length > 0 && { membershipRemovals: [...removals.memberships] })
  };
}

/**
 * Pending entity removals actually due on the wire: queued ids minus any the
 * table still shows (a row cannot be re-added by hand, but the queue is
 * defensive against double-queuing), deduplicated.
 */
export function pendingEntityRemovals(
  removedEntityIds: readonly string[],
  rows: readonly ParsedOrgFunctionRow[]
): string[] {
  const visibleIds = new Set(rows.map((row) => row.entityId).filter(Boolean));
  return [...new Set(removedEntityIds)].filter((id) => !visibleIds.has(id));
}

const membershipRemovalKey = (removal: InitializeMembershipRemoval) =>
  `${removal.orgFunctionId}#${normalizedKey(removal.memberEmail)}`;

/**
 * Pending membership removals actually due on the wire: queued unlinks minus
 * any pair re-entered in the current table — re-adding the same membership
 * before Weiter must win over the earlier delete. Current rows are compared
 * by their resolved link (function id + lowercased email), the same identity
 * the queued removal targets.
 */
export function pendingMembershipRemovals(
  membershipRemovals: readonly InitializeMembershipRemoval[],
  memberships: readonly ParsedMembershipRow[],
  idByFunctionName: ReadonlyMap<string, string>
): InitializeMembershipRemoval[] {
  const currentKeys = new Set(
    memberships.flatMap((row) => {
      const orgFunctionId = idByFunctionName.get(normalizedKey(row.data.orgFunctionName));
      return orgFunctionId
        ? [membershipRemovalKey({ orgFunctionId, memberEmail: row.data.memberEmail })]
        : [];
    })
  );
  const seen = new Set<string>();
  return membershipRemovals.filter((removal) => {
    const key = membershipRemovalKey(removal);
    if (currentKeys.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export interface CommittedSurface {
  /** Server id per SENT row (created, skipped and updated alike), by row identity. */
  idByRow: Map<ParsedOrgFunctionRow, string>;
  membershipsLinked: number;
  /** Rows created (not skipped/updated) across both kind arrays. */
  createdCount: number;
}

/**
 * Sends a Funktionen/Teams surface draft and zips the server's per-row
 * results back onto the sent rows. A create that collided with an existing
 * OrgFunction (`created: false`, no entityId on the row) is re-sent as an
 * in-place `_id` update in a follow-up request — referencing an existing
 * Funktion/Team edits it, the server-side skip must never drop the row's
 * changes.
 */
export async function commitOrgFunctionSurface(config: {
  kind: 'role' | 'team';
  rows: readonly ParsedOrgFunctionRow[];
  memberships: readonly ParsedMembershipRow[];
  departments: Department[];
  /** Weiter-only: the silent flush never sends removals. */
  removals?: {
    entityIds: readonly string[];
    memberships: readonly InitializeMembershipRemoval[];
  };
  mutate: (request: InitializeAddOrgFunctionsRequest) => Promise<InitializeAddOrgFunctionsResponse>;
}): Promise<CommittedSurface> {
  const { kind, rows, memberships, departments, removals, mutate } = config;
  const response = await mutate(
    buildOrgFunctionsRequest({ kind, rows, memberships, departments, removals })
  );
  const results: CommittedRow[] = kind === 'role' ? response.functions : response.teams;

  const idByRow = new Map<ParsedOrgFunctionRow, string>();
  const collisions: ParsedOrgFunctionRow[] = [];
  rows.forEach((row, index) => {
    const result = results[index];
    if (!result) return;
    idByRow.set(row, result._id);
    if (!result.created && !row.entityId) {
      collisions.push({ ...row, entityId: result._id });
    }
  });
  if (collisions.length > 0) {
    await mutate(
      buildOrgFunctionsRequest({ kind, rows: collisions, memberships: [], departments })
    );
  }

  return {
    idByRow,
    membershipsLinked: response.memberships.linked,
    createdCount: [...response.functions, ...response.teams].filter((result) => result.created)
      .length
  };
}

/** Merge the returned ids into rows by identity, leaving edited rows alone. */
export function stampSurfaceRows(
  rows: readonly ParsedOrgFunctionRow[],
  idByRow: Map<ParsedOrgFunctionRow, string>
): ParsedOrgFunctionRow[] {
  return rows.map((row) => {
    const id = idByRow.get(row);
    return id && !row.entityId ? { ...row, entityId: id } : row;
  });
}
